import { Link, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import {
  FaChalkboardTeacher,
  FaUserFriends,
  FaMoneyBillWave,
  FaClipboardList,
  FaChartLine,
  FaCalendarAlt,
  FaCheckCircle,
  FaBell,
  FaChevronRight,
  FaFire,
  FaMedal,
  FaBriefcase,
  FaToggleOff,
  FaExclamationTriangle,
  FaRedo,
  FaSpinner,
  FaInbox,
  FaHourglassHalf,
  FaCrown,
  FaStar
} from 'react-icons/fa';
import { RiMedalLine } from 'react-icons/ri';
import yogBg from '../../images/yg1.jpg';
import UserContext from '../../Components/UserContext';
import { useContext } from 'react';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import { useDateTimeSettings } from '../../hooks/useDateTimeSettings';
import { formatDate as formatDateUtil, formatTime as formatTimeUtil } from '../../utils/dateFormatter';
import { formatCurrency } from '../../utils/currencyFormatter';

function HomeTrainer() {
  const { session } = useContext(UserContext);
  const navigate = useNavigate();
  const dateTimeSettings = useDateTimeSettings();
  const [isPWA, setIsPWA] = useState(false);
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);

  // Get service context
  const trainerServices = (session?.services && session.services.length > 0) ? session.services : (session?.service ? [session.service] : []);
  const servicePartnerTypes = session?.servicePartnerTypes || {};
  const activeService = localStorage.getItem('activeService') || trainerServices[0] || '';
  const activePartnerType = activeService ? (servicePartnerTypes[activeService] || 'instructor') : 'instructor';
  const isConsultant = activePartnerType === 'consultant';

  // Check if trainer is on advance plan
  const isAdvancePlan = session?.subscriptionPlan === 'advance';

  const [stats, setStats] = useState({
    totalStudents: 0,
    activePrograms: 0,
    monthlyRevenue: 0,
    completionRate: 0,
    upcomingClasses: 0,
    pendingPayments: 0,
    streakDays: 0,
    sessionsTaught: 0,
    // Consultant-specific stats
    pendingAppointments: 0,
    upcomingAppointments: 0,
    totalAppointments: 0
  });

  // Monthly appointments goal (can be made configurable later)
  const MONTHLY_GOAL = 35; // Default goal: 35 appointments per month
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Fetch trainer dashboard stats
  const fetchStats = async (isRetry = false) => {
    try {
      setStatsError(null);
      setIsLoadingStats(true);

      // Check if session exists before making request (prevent errors during logout)
      if (!session) {
        console.log('No session available, skipping stats fetch');
        return;
      }

      const response = await HttpInterceptor.get('/api/trainer/dashboard/stats', {
        showErrorToast: false // Suppress error toast - we'll handle it manually
      });
      if (response.status === 200) {
        const statsData = response.data?.data || response.data || {};
        setStats({
          totalStudents: statsData.totalStudents || 0,
          activePrograms: statsData.activePrograms || 0,
          monthlyRevenue: statsData.monthlyRevenue || 0,
          completionRate: statsData.completionRate || 0,
          upcomingClasses: statsData.upcomingClasses || 0,
          pendingPayments: statsData.pendingPayments || 0,
          streakDays: statsData.streakDays || 0,
          sessionsTaught: statsData.sessionsTaught || 0,
          // Consultant-specific stats
          pendingAppointments: statsData.pendingAppointments || 0,
          upcomingAppointments: statsData.upcomingAppointments || 0,
          totalAppointments: statsData.totalAppointments || 0
        });
        setRetryCount(0); // Reset retry count on success
      }

      // Also fetch appointment stats if consultant
      if (isConsultant && activeService) {
        try {
          const appointmentResponse = await HttpInterceptor.get('/api/appointments', {
            params: { service: activeService }
          });
          if (appointmentResponse.status === 200) {
            const appointments = appointmentResponse.data.data || [];
            const pending = appointments.filter(apt => apt.status === 'pending').length;
            const upcoming = appointments.filter(apt =>
              ['approved', 'confirmed'].includes(apt.status) &&
              new Date(apt.requestedDateTime) >= new Date()
            ).length;
            const validAppointments = appointments.filter(apt => !['rejected', 'cancelled'].includes(apt.status));
            setStats(prev => ({
              ...prev,
              pendingAppointments: pending,
              upcomingAppointments: upcoming,
              totalAppointments: validAppointments.length
            }));
          }
        } catch (err) {
          // Don't fail dashboard if appointment stats fail
          console.error('Error fetching appointment stats:', err);
        }
      }
    } catch (err) {
      // Don't show error if session is null (user is logging out) or if it's a 401 (unauthorized)
      // This prevents error toast from appearing during logout
      if (!session || err.response?.status === 401) {
        console.log('Stats fetch failed due to logout or unauthorized - suppressing error');
        setIsLoadingStats(false);
        return;
      }

      console.error('Error fetching trainer stats:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load dashboard statistics';

      // Set default values on error
      setStats({
        totalStudents: 0,
        activePrograms: 0,
        monthlyRevenue: 0,
        completionRate: 0,
        upcomingClasses: 0,
        pendingPayments: 0,
        streakDays: 0,
        sessionsTaught: 0
      });

      if (retryCount < MAX_RETRIES && !isRetry) {
        setStatsError({
          message: errorMessage,
          retryable: true
        });
        toast.error('Failed to load dashboard stats. Please try again.');
      } else {
        setStatsError({
          message: 'Dashboard statistics are currently unavailable. Some features may not display correctly.',
          retryable: false
        });
      }
    } finally {
      setIsLoadingStats(false);
    }
  };

  const [setupGuideStatus, setSetupGuideStatus] = useState({
    hasPrograms: false,
    hasClasses: false,
    hasFeePlans: false,
    hasStudents: false
  });
  const [isLoadingSetupGuide, setIsLoadingSetupGuide] = useState(true);

  // Fetch setup guide status
  const fetchSetupGuideStatus = useCallback(async () => {
    try {
      setIsLoadingSetupGuide(true);
      const response = await HttpInterceptor.get('/api/trainer/setup-guide-status');
      if (response.status === 200) {
        const data = response.data?.data || response.data || {};
        setSetupGuideStatus({
          hasPrograms: data.hasPrograms || false,
          hasClasses: data.hasClasses || false,
          hasFeePlans: data.hasFeePlans || false,
          hasStudents: data.hasStudents || false
        });
      }
    } catch (err) {
      console.error('Error fetching setup guide status:', err);
      // On error, set all to false (not completed)
      setSetupGuideStatus({
        hasPrograms: false,
        hasClasses: false,
        hasFeePlans: false,
        hasStudents: false
      });
    } finally {
      setIsLoadingSetupGuide(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchSetupGuideStatus();
  }, [fetchSetupGuideStatus]);

  const handleRetryStats = () => {
    setRetryCount(prev => prev + 1);
    fetchStats(true);
  };

  // Workflow steps with completion status from API - conditional based on partner type
  const workflowSteps = [
    {
      title: 'Add Program',
      icon: <FaClipboardList className="text-xl" />,
      link: '/trainer/consultant-programs',
      completed: setupGuideStatus.hasPrograms,
      description: isConsultant ? 'Create consultation programs with time windows' : 'Create yoga programs with different levels'
    },
    {
      title: 'Create Fee Plan',
      icon: <FaMoneyBillWave className="text-xl" />,
      link: '/trainer/fee-plan',
      completed: setupGuideStatus.hasFeePlans,
      description: 'Define pricing for your programs'
    },
    ...(isConsultant ? [{
      title: 'Manage Appointments',
      icon: <FaCalendarAlt className="text-xl" />,
      link: '/trainer/appointments',
      completed: setupGuideStatus.hasClasses, // Reuse hasClasses flag for appointments
      description: 'Review and approve appointment requests'
    }] : [{
      title: 'Schedule Classes',
      icon: <FaCalendarAlt className="text-xl" />,
      link: '/trainer/classes',
      completed: setupGuideStatus.hasClasses,
      description: 'Set up class timings and sessions'
    }]),
    {
      title: isConsultant ? 'View Clients' : 'Add Students',
      icon: <FaUserFriends className="text-xl" />,
      link: '/trainer/students',
      completed: setupGuideStatus.hasStudents,
      description: isConsultant ? 'View and manage your clients' : 'Enroll students to your programs'
    }
  ];

  const [featuredStudents, setFeaturedStudents] = useState([]);
  const [isLoadingStudents, setIsLoadingStudents] = useState(true);
  const [studentsError, setStudentsError] = useState(null);

  const [activities, setActivities] = useState([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(true);
  const [activitiesError, setActivitiesError] = useState(null);

  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [isLoadingUpcomingClasses, setIsLoadingUpcomingClasses] = useState(true);
  const [upcomingClassesError, setUpcomingClassesError] = useState(null);

  // Fetch featured students (for instructors) or clients (for consultants)
  const fetchFeaturedStudents = async () => {
    try {
      setStudentsError(null);
      setIsLoadingStudents(true);

      if (isConsultant && activeService) {
        // For consultants: fetch appointments and group by client
        const appointmentResponse = await HttpInterceptor.get('/api/appointments', {
          params: { service: activeService }
        });

        if (appointmentResponse.status === 200) {
          const appointments = appointmentResponse.data.data || [];

          // Filter out rejected and cancelled appointments
          const activeAppointments = appointments.filter(apt => !['rejected', 'cancelled'].includes(apt.status));

          // Group appointments by member/client
          const clientMap = new Map();

          activeAppointments.forEach((apt) => {
            const memberId = apt.member?._id || apt.member?.id || 'unknown';
            const memberName = apt.member?.name || 'Unknown Client';
            const memberImage = apt.member?.image || apt.member?.user?.image || null;

            if (!clientMap.has(memberId)) {
              clientMap.set(memberId, {
                memberId,
                name: memberName,
                image: memberImage,
                appointments: [],
                totalRevenue: 0,
                lastAppointmentDate: null,
                upcomingAppointments: 0
              });
            }

            const client = clientMap.get(memberId);
            client.appointments.push(apt);

            // Calculate total revenue (only from completed/confirmed appointments)
            if (['completed', 'confirmed', 'approved'].includes(apt.status) && apt.feeAmount) {
              client.totalRevenue += apt.feeAmount;
            }

            // Track last appointment date
            if (apt.requestedDateTime) {
              const aptDate = new Date(apt.requestedDateTime);
              if (!client.lastAppointmentDate || aptDate > client.lastAppointmentDate) {
                client.lastAppointmentDate = aptDate;
              }
            }

            // Count upcoming appointments
            if (['approved', 'confirmed'].includes(apt.status) && apt.requestedDateTime) {
              const aptDate = new Date(apt.requestedDateTime);
              if (aptDate >= new Date()) {
                client.upcomingAppointments++;
              }
            }
          });

          // Transform to display format and sort by last appointment date (most recent first)
          const transformedClients = Array.from(clientMap.values())
            .sort((a, b) => {
              if (!a.lastAppointmentDate && !b.lastAppointmentDate) return 0;
              if (!a.lastAppointmentDate) return 1;
              if (!b.lastAppointmentDate) return -1;
              return b.lastAppointmentDate - a.lastAppointmentDate;
            })
            .slice(0, 3)
            .map((client) => {
              // Get image from member data
              let validImage = null;
              if (client.image && typeof client.image === 'string') {
                const trimmed = client.image.trim();
                if (trimmed !== '' && trimmed !== '/' && !trimmed.startsWith('/src/images/')) {
                  validImage = trimmed;
                }
              }

              // Format last appointment date
              let lastAppointment = 'No appointments';
              if (client.lastAppointmentDate) {
                const date = client.lastAppointmentDate;
                const now = new Date();
                const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
                if (diffDays === 0) lastAppointment = 'Today';
                else if (diffDays === 1) lastAppointment = 'Yesterday';
                else if (diffDays > 1 && diffDays < 7) lastAppointment = `${diffDays} days ago`;
                else if (diffDays < 0) lastAppointment = `In ${Math.abs(diffDays)} days`;
                else lastAppointment = formatDateUtil(date, dateTimeSettings.dateFormat);
              }

              // Get the most common program name from appointments
              const programCounts = {};
              client.appointments.forEach(apt => {
                const programName = apt.program?.name || 'Consultation';
                programCounts[programName] = (programCounts[programName] || 0) + 1;
              });
              const mostCommonProgram = Object.keys(programCounts).length > 0
                ? Object.keys(programCounts).reduce((a, b) =>
                  programCounts[a] > programCounts[b] ? a : b
                )
                : 'Consultation';

              return {
                name: client.name,
                program: mostCommonProgram,
                totalAppointments: client.appointments.length,
                lastAppointment,
                totalRevenue: client.totalRevenue,
                upcomingAppointments: client.upcomingAppointments,
                image: validImage
              };
            });

          setFeaturedStudents(transformedClients);
        } else {
          setFeaturedStudents([]);
        }
      } else {
        // For instructors: fetch students as before
        const response = await HttpInterceptor.get('/api/students?limit=3');
        const studentsData = response.data?.data?.students || response.data || [];

        // Ensure studentsData is an array before using slice
        const studentsArray = Array.isArray(studentsData) ? studentsData : [];

        // Transform students data for display
        const transformedStudents = studentsArray.slice(0, 3).map((student) => {
          // Get image from multiple possible sources
          const image = student.image || student.user?.image || student.personalDetails?.image || null;
          // Ensure image is a valid URL string (not empty string or invalid path)
          let validImage = null;
          if (image && typeof image === 'string') {
            const trimmed = image.trim();
            // Check if it's a valid URL or path (not empty, not just a slash)
            if (trimmed !== '' && trimmed !== '/' && !trimmed.startsWith('/src/images/')) {
              validImage = trimmed;
            }
          }

          // Debug logging
          if (process.env.NODE_ENV === 'development') {
            console.log('[HomeTrainer] Student image data:', {
              name: student.personalDetails?.name || student.name,
              image: student.image,
              userImage: student.user?.image,
              personalDetailsImage: student.personalDetails?.image,
              validImage
            });
          }

          return {
            name: student.personalDetails?.name || student.name || 'Student',
            program: student.program?.programId?.name || student.program?.name || 'Program',
            progress: student.attendance?.attendancePercentage || 0,
            lastSession: student.attendance?.lastSession
              ? (() => {
                const date = new Date(student.attendance.lastSession);
                const now = new Date();
                const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
                if (diffDays === 0) return 'Today';
                if (diffDays === 1) return 'Yesterday';
                if (diffDays < 7) return `${diffDays} days ago`;
                return formatDateUtil(date, dateTimeSettings.dateFormat);
              })()
              : 'No sessions',
            image: validImage,
            level: student.program?.level || 'Beginner',
            sessions: student.attendance?.attendedSessions || 0
          };
        });

        setFeaturedStudents(transformedStudents);
      }
    } catch (err) {
      console.error('Error fetching featured students/clients:', err);
      setStudentsError('Unable to load data');
      setFeaturedStudents([]);
    } finally {
      setIsLoadingStudents(false);
    }
  };

  // Fetch recent activities from transactions
  const fetchActivities = async () => {
    try {
      setActivitiesError(null);
      setIsLoadingActivities(true);
      const response = await HttpInterceptor.get('/api/transactions?limit=5');
      const transactions = Array.isArray(response.data) ? response.data : (response.data?.data || []);

      // Ensure transactions is an array before using slice
      const transactionsArray = Array.isArray(transactions) ? transactions : [];

      // Transform transactions to activities
      const activitiesList = transactionsArray.slice(0, 5).map((tx) => ({
        type: 'payment',
        student: tx.studentName || (isConsultant ? 'Client' : 'Student'),
        amount: tx.finalAmount || tx.amount || 0,
        time: tx.date ? formatDateUtil(tx.date, dateTimeSettings.dateFormat) : 'Recently',
        icon: <FaMoneyBillWave className="text-green-500" />,
        status: tx.status || 'completed'
      }));

      setActivities(activitiesList);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setActivitiesError('Unable to load activities');
      setActivities([]);
    } finally {
      setIsLoadingActivities(false);
    }
  };

  // Helper function to calculate next occurrence of a class
  const getNextOccurrence = (day, time) => {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const targetDay = daysOfWeek.indexOf(day);

    // Calculate days until next occurrence
    let daysUntil = targetDay - currentDay;
    if (daysUntil < 0) {
      daysUntil += 7; // Next week
    } else if (daysUntil === 0) {
      // Same day - check if time has passed
      const [hours, minutes] = time.split(':').map(Number);
      const classTime = new Date(today);
      classTime.setHours(hours, minutes, 0, 0);
      if (classTime < today) {
        daysUntil = 7; // Next week
      }
    }

    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntil);
    const [hours, minutes] = time.split(':').map(Number);
    nextDate.setHours(hours, minutes, 0, 0);

    return nextDate;
  };

  // Helper function to format date for display
  const formatUpcomingDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const classDate = new Date(date);
    classDate.setHours(0, 0, 0, 0);

    const diffDays = Math.round((classDate - today) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Tomorrow';
    } else {
      // Use user's date format for dates beyond tomorrow
      return formatDateUtil(date, dateTimeSettings.dateFormat);
    }
  };

  // Fetch upcoming classes (for instructors) or appointments (for consultants)
  const fetchUpcomingClasses = useCallback(async () => {
    try {
      setUpcomingClassesError(null);
      setIsLoadingUpcomingClasses(true);

      // Check if user is an approved business partner
      if (!session?.isTrainer || session?.trainerStatus !== 'active') {
        setUpcomingClasses([]);
        setIsLoadingUpcomingClasses(false);
        return;
      }

      // For consultants: fetch upcoming appointments
      if (isConsultant) {
        try {
          const response = await HttpInterceptor.get('/api/appointments', {
            params: {
              status: 'approved,confirmed',
              limit: 3
            }
          });

          const appointments = Array.isArray(response.data?.data) ? response.data.data : [];

          // Filter and format upcoming appointments
          const now = new Date();

          const upcomingAppointments = appointments
            .filter(apt => {
              const aptDate = new Date(apt.requestedDateTime || apt.appointmentDate || apt.date);
              return aptDate >= now;
            })
            .sort((a, b) => {
              const dateA = new Date(a.requestedDateTime || a.appointmentDate || a.date);
              const dateB = new Date(b.requestedDateTime || b.appointmentDate || b.date);
              return dateA - dateB;
            })
            .slice(0, 3)
            .map(apt => {
              const dateVal = apt.requestedDateTime || apt.appointmentDate || apt.date;
              return {
                _id: apt._id,
                appointmentDate: dateVal,
                slotDuration: apt.slotDuration || apt.duration || '1 hour',
                programName: apt.program?.name || 'Consultation',
                clientName: apt.client?.name || apt.member?.name || 'Client',
                status: apt.status,
                nextOccurrence: new Date(dateVal)
              };
            });

          setUpcomingClasses(upcomingAppointments);
        } catch (err) {
          console.error('Error fetching upcoming appointments:', err);
          setUpcomingClassesError('Unable to load upcoming appointments');
          setUpcomingClasses([]);
        } finally {
          setIsLoadingUpcomingClasses(false);
        }
        return;
      }

      // For instructors: fetch upcoming classes (existing logic)
      // Fetch all programs for the trainer
      const programsResponse = await HttpInterceptor.get('/api/programs', {
        params: { trainerId: session?.trainerId || session?.id }
      });
      const programs = Array.isArray(programsResponse.data) ? programsResponse.data : [];

      if (programs.length === 0) {
        setUpcomingClasses([]);
        setIsLoadingUpcomingClasses(false);
        return;
      }

      // Fetch classes for each active program
      const classesPromises = programs
        .filter(p => p.isActive)
        .map(program =>
          HttpInterceptor.get('/api/classes', {
            params: { programId: program._id }
          }).catch(err => {
            console.warn(`Failed to fetch classes for program ${program._id}:`, err);
            return { data: [] };
          })
        );

      const classesResponses = await Promise.all(classesPromises);
      const allClasses = classesResponses.flatMap(response =>
        Array.isArray(response.data) ? response.data : []
      );

      // Filter only active classes and calculate next occurrence
      const classesWithNextOccurrence = allClasses
        .filter(cls => cls.isActive !== false)
        .map(cls => {
          // Find the program for this class to get effective date
          const program = programs.find(p => p._id === cls.programId?._id || p._id === cls.programId);
          const programEffectiveDate = program?.effectiveDate ? new Date(program.effectiveDate) : null;

          // Calculate next occurrence
          let nextOccurrence = getNextOccurrence(cls.day, cls.time);

          // If program has an effective date and next occurrence is before it, find the first occurrence on or after effective date
          if (programEffectiveDate && nextOccurrence < programEffectiveDate) {
            // Find the first occurrence of this class day/time on or after the program effective date
            const effectiveDate = new Date(programEffectiveDate);
            effectiveDate.setHours(0, 0, 0, 0);

            const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const targetDayIndex = daysOfWeek.indexOf(cls.day);
            const effectiveDayIndex = effectiveDate.getDay();

            // Calculate days until the target day of week
            let daysUntil = targetDayIndex - effectiveDayIndex;
            if (daysUntil < 0) {
              daysUntil += 7; // Next week
            }

            // If it's the same day as effective date
            if (daysUntil === 0) {
              // Only check if time has passed if effective date is today
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isEffectiveDateToday = effectiveDate.getTime() === today.getTime();

              if (isEffectiveDateToday) {
                // Effective date is today - check if the time has passed
                const [hours, minutes] = cls.time.split(':').map(Number);
                const classTime = new Date(effectiveDate);
                classTime.setHours(hours, minutes, 0, 0);
                const now = new Date();
                if (classTime < now) {
                  daysUntil = 7; // Next week (time has passed today)
                }
              }
              // If effective date is in the future and it's the same day, use that day (daysUntil stays 0)
            }

            nextOccurrence = new Date(effectiveDate);
            nextOccurrence.setDate(effectiveDate.getDate() + daysUntil);
            const [hours, minutes] = cls.time.split(':').map(Number);
            nextOccurrence.setHours(hours, minutes, 0, 0);
          }

          return {
            ...cls,
            nextOccurrence,
            programName: program?.name || 'Program'
          };
        })
        .filter(cls => {
          // Only include classes that occur in the future (on or after today)
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const classDate = new Date(cls.nextOccurrence);
          classDate.setHours(0, 0, 0, 0);
          return classDate >= today;
        })
        .sort((a, b) => a.nextOccurrence - b.nextOccurrence)
        .slice(0, 3); // Get top 3 upcoming classes

      setUpcomingClasses(classesWithNextOccurrence);
    } catch (err) {
      console.error('Error fetching upcoming classes:', err);
      setUpcomingClassesError('Unable to load upcoming classes');
      setUpcomingClasses([]);
    } finally {
      setIsLoadingUpcomingClasses(false);
    }
  }, [session?.isTrainer, session?.trainerStatus, session?.trainerId, session?.id, isConsultant]);

  useEffect(() => {
    fetchFeaturedStudents();
    fetchActivities();
    fetchUpcomingClasses();
  }, [fetchUpcomingClasses]);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Quick actions - optimized for PWA (remove redundant descriptions)
  // Navigation items for PWA mode (replaces quick actions)
  const navigationItems = [
    {
      title: 'Programs',
      icon: <FaClipboardList className="text-orange-600" />,
      link: '/trainer/consultant-programs',
      bgColor: 'bg-gradient-to-br from-teal-50 via-emerald-50/80 to-white',
      textColor: 'text-orange-800',
      iconBg: 'bg-gradient-to-br from-orange-100 to-amber-100',
      borderColor: 'border-orange-200/60'
    },
    // Conditionally show Sessions (instructor) or Appointments (consultant)
    ...(isConsultant ? [{
      title: 'Appointments',
      icon: <FaCalendarAlt className="text-amber-600" />,
      link: '/trainer/appointments',
      bgColor: 'bg-gradient-to-br from-blue-50 via-teal-50/80 to-white',
      textColor: 'text-amber-800',
      iconBg: 'bg-gradient-to-br from-blue-100 to-teal-100',
      borderColor: 'border-blue-200/60'
    }] : [{
      title: 'Sessions',
      icon: <FaChalkboardTeacher className="text-amber-600" />,
      link: '/trainer/classes',
      bgColor: 'bg-gradient-to-br from-blue-50 via-teal-50/80 to-white',
      textColor: 'text-amber-800',
      iconBg: 'bg-gradient-to-br from-blue-100 to-teal-100',
      borderColor: 'border-blue-200/60'
    }]),
    {
      title: 'Fee Plan',
      icon: <FaMoneyBillWave className="text-amber-600" />,
      link: '/trainer/fee-plan',
      bgColor: 'bg-gradient-to-br from-amber-50 via-yellow-50/80 to-white',
      textColor: 'text-amber-800',
      iconBg: 'bg-gradient-to-br from-amber-100 to-yellow-100',
      borderColor: 'border-amber-200/60'
    },
    {
      title: 'My Members',
      icon: <FaUserFriends className="text-yellow-600" />,
      link: '/trainer/students',
      bgColor: 'bg-gradient-to-br from-indigo-50 via-purple-50/80 to-white',
      textColor: 'text-indigo-800',
      iconBg: 'bg-gradient-to-br from-indigo-100 to-purple-100',
      borderColor: 'border-yellow-200/60'
    },
    {
      title: 'Fees',
      icon: <FaMoneyBillWave className="text-orange-600" />,
      link: '/trainer/fees',
      bgColor: 'bg-gradient-to-br from-orange-50 via-amber-50/80 to-white',
      textColor: 'text-orange-800',
      iconBg: 'bg-gradient-to-br from-orange-100 to-amber-100',
      borderColor: 'border-orange-200/60'
    },
    {
      title: 'Notifications',
      icon: <FaBell className="text-yellow-600" />,
      link: '/trainer/notifications',
      bgColor: 'bg-gradient-to-br from-indigo-50 via-blue-50/80 to-white',
      textColor: 'text-indigo-800',
      iconBg: 'bg-gradient-to-br from-indigo-100 to-blue-100',
      borderColor: 'border-yellow-200/60'
    }
  ];

  // Quick actions - for non-PWA mode only
  const quickActions = [
    {
      title: 'Programs',
      icon: <FaClipboardList className="text-2xl text-green-500" />,
      link: '/trainer/consultant-programs',
      desc: 'Create new programs',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    // Conditionally show Classes (instructor) or Appointments (consultant)
    ...(isConsultant ? [{
      title: 'Appointments',
      icon: <FaCalendarAlt className="text-2xl text-blue-500" />,
      link: '/trainer/appointments',
      desc: 'Manage appointments',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600'
    }] : [{
      title: 'Classes',
      icon: <FaChalkboardTeacher className="text-2xl text-blue-500" />,
      link: '/trainer/classes',
      desc: 'Manage your schedule',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600'
    }]),
    {
      title: 'Fee Plan',
      icon: <FaMoneyBillWave className="text-2xl text-red-500" />,
      link: '/trainer/fee-plan',
      desc: 'Manage pricing plans',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600'
    },
    {
      title: isConsultant ? 'Clients' : 'Students',
      icon: <FaUserFriends className="text-2xl text-purple-500" />,
      link: '/trainer/students',
      desc: isConsultant ? 'View all clients' : 'View all students',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      title: 'Payments',
      icon: <FaMoneyBillWave className="text-2xl text-yellow-500" />,
      link: '/trainer/fees',
      desc: 'Track payments',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    },
    {
      title: 'Notifications',
      icon: <FaBell className="text-2xl text-indigo-500" />,
      link: '/trainer/notifications',
      desc: 'View messages',
      bgColor: 'bg-indigo-50',
      textColor: 'text-yellow-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{ backgroundImage: `url(${yogBg})` }}
        />
        <div className={`relative z-10 ${isPWA ? 'bg-gradient-to-br from-orange-900/40 via-amber-900/35 to-yellow-900/40' : 'bg-gradient-to-r from-black/70 to-black/50'} ${isPWA ? 'py-8 sm:py-10' : 'py-24'}`}>
          {isPWA && (
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"></div>
          )}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {stats.streakDays > 0 && (
              <div className="flex justify-center items-center mb-3 sm:mb-4">
                <div className={`${isPWA ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-yellow-900 shadow-lg' : 'bg-yellow-400/90 text-yellow-900'} ${isPWA ? 'px-3 py-1 text-xs' : 'px-4 py-1 text-sm'} rounded-full font-semibold flex items-center ${isPWA ? 'ring-2 ring-yellow-300/50' : ''}`}>
                  <FaFire className={isPWA ? 'mr-1' : 'mr-1'} /> {stats.streakDays}-day streak
                </div>
              </div>
            )}
            <h1 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-4xl md:text-5xl'} font-bold text-white ${isPWA ? 'mb-3' : 'mb-4'} ${isPWA ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]' : ''}`}>
              {isPWA ? (
                <>
                  <span className="text-white capitalize drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)]">{session?.name || 'Trainer'}</span>
                  <span className="block text-sm text-white/90 mt-1 font-normal drop-shadow-[0_1px_6px_rgba(0,0,0,0.8)]">Professional Dashboard</span>
                </>
              ) : (
                <>Hi! Welcome, <span className="text-yellow-300 capitalize">{session?.name || 'Trainer'}</span></>
              )}
            </h1>
            {!isPWA && (
              <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-6">
                Your dashboard for managing students, programs, and payments - all in one place.
              </p>
            )}
            <div className={`flex justify-center ${isPWA ? 'gap-2 flex-wrap' : 'gap-4'}`}>
              <div className={`${isPWA ? 'bg-black/40 backdrop-blur-sm border border-white/30 shadow-lg' : 'bg-white/10 backdrop-blur-sm'} ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'} rounded-lg flex items-center`}>
                <FaChalkboardTeacher className={`${isPWA ? 'text-orange-300' : 'text-blue-400'} ${isPWA ? 'mr-1.5 text-sm' : 'mr-2'}`} />
                <span className="text-white whitespace-nowrap font-medium drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">{stats.sessionsTaught} sessions</span>
              </div>
              <div className={`${isPWA ? 'bg-black/40 backdrop-blur-sm border border-white/30 shadow-lg' : 'bg-white/10 backdrop-blur-sm'} ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'} rounded-lg flex items-center`}>
                <RiMedalLine className={`${isPWA ? 'text-yellow-300' : 'text-yellow-300'} ${isPWA ? 'mr-1.5 text-sm' : 'mr-2'}`} />
                <span className="text-white whitespace-nowrap font-medium drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
                  Level {stats.sessionsTaught > 0 ? Math.floor(stats.sessionsTaught / 10) + 1 : 1}
                </span>
              </div>
              {isAdvancePlan && (
                <div className={`${isPWA ? 'bg-gradient-to-r from-yellow-400/90 to-orange-400/90 border border-yellow-300/50' : 'bg-gradient-to-r from-yellow-400/80 to-orange-400/80'} ${isPWA ? 'px-3 py-1.5 text-xs' : 'px-4 py-2'} rounded-lg flex items-center shadow-lg backdrop-blur-sm`}>
                  <FaCrown className={`${isPWA ? 'text-yellow-900' : 'text-yellow-900'} ${isPWA ? 'mr-1.5 text-sm' : 'mr-2'}`} />
                  <span className="text-yellow-900 whitespace-nowrap font-semibold drop-shadow-[0_1px_2px_rgba(0,0,0,0.3)]">
                    {isPWA ? 'Advance' : 'Advance Plan'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Toggle Dashboard View - Show if approved business partner - Compact in PWA */}
      {session?.isTrainer && session?.trainerStatus === 'active' && (
        <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? '-mt-6 sm:-mt-4' : '-mt-8'} z-20 relative ${isPWA ? 'mb-3' : 'mb-4'}`}>
          <div className={`${isPWA ? 'bg-gradient-to-r from-orange-50 via-amber-50 to-yellow-50 border-2 border-orange-200 shadow-lg' : 'bg-white shadow-sm'} rounded-xl ${isPWA ? 'p-3' : 'p-4'} relative overflow-hidden`}>
            {isPWA && (
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-200/30 to-amber-200/20 rounded-full blur-2xl"></div>
            )}
            <div className={`flex items-center ${isPWA ? 'flex-col sm:flex-row gap-2 sm:gap-2' : 'justify-between flex-wrap gap-4'} relative z-10`}>
              <div className={`flex items-center ${isPWA ? 'w-full sm:w-auto' : ''}`}>
                <div className={`${isPWA ? 'bg-gradient-to-br from-orange-500 to-amber-600 p-2 rounded-lg shadow-md' : ''}`}>
                  <FaBriefcase className={`${isPWA ? 'text-white' : 'text-orange-600'} ${isPWA ? 'text-base' : 'mr-3 text-xl'}`} />
                </div>
                <div className={`${isPWA ? 'ml-2' : ''} flex-1 min-w-0`}>
                  <h3 className={`${isPWA ? 'text-orange-800' : 'text-gray-800'} font-bold ${isPWA ? 'text-sm' : ''}`}>Professional Dashboard</h3>
                  {!isPWA && <p className="text-gray-600 text-sm">Switch between professional and member views</p>}
                  {isPWA && (
                    <p className="text-xs text-orange-600 mt-0.5">Manage your business</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowSwitchConfirm(true)}
                title="Switch from (Professional to Member) dashboard"
                className={`flex items-center gap-1.5 ${isPWA ? 'px-3 py-1.5 text-xs sm:text-sm w-full sm:w-auto justify-center' : 'px-4 py-2'} ${isPWA ? 'bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-500 hover:to-slate-600 shadow-md' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-lg transition-all ${isPWA ? 'border border-slate-500/50' : ''}`}
              >
                <span className={isPWA ? 'truncate font-medium' : ''}>Switch to Member View</span>
                <FaToggleOff className={isPWA ? 'text-base flex-shrink-0' : 'text-xl'} />
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Compact Setup Guide - Hidden in PWA mode */}
      {!isPWA && (
        <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${session?.isTrainer && session?.trainerStatus === 'active' ? '' : '-mt-8'} z-20 relative`}>
          <div className="bg-white rounded-xl shadow-sm p-6 transition-all duration-300 hover:shadow-md">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Your Setup Guide</h2>
            <div className="flex items-center justify-between">
              {workflowSteps.map((step, index) => (
                <div key={index} className="flex flex-col items-center relative flex-1">
                  {/* Connector lines between steps */}
                  {index > 0 && (
                    <div className={`absolute h-1 w-16 top-1/2 -left-16 -translate-y-1/2 ${step.completed || workflowSteps[index - 1].completed ? 'bg-green-500' : 'bg-gray-200'}`}></div>
                  )}

                  {/* Step icon - clickable only if not completed */}
                  {step.completed ? (
                    <div className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-green-100 text-green-600">
                      <FaCheckCircle className="text-lg" />
                    </div>
                  ) : (
                    <Link
                      to={step.link}
                      className="flex items-center justify-center w-10 h-10 rounded-full mb-2 bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700"
                    >
                      {step.icon}
                    </Link>
                  )}

                  <div className="text-center">
                    <h3 className={`text-sm font-medium ${step.completed ? 'text-green-600' : 'text-gray-700'}`}>
                      {step.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 hidden md:block">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Stats Overview */}
      <section className={`${isPWA ? 'py-6 sm:py-8 pb-10 sm:pb-12' : 'py-12'} ${isPWA ? 'bg-gradient-to-b from-orange-50/40 via-amber-50/30 to-white' : 'bg-white'} relative`}>
        {isPWA && (
          <>
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-200/40 to-transparent"></div>
          </>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center ${isPWA ? 'mb-6 sm:mb-8 px-2' : 'mb-8 px-4'}`}>
            <div className="flex items-center gap-3 relative">
              {isPWA && (
                <>
                  <div className="absolute left-0 w-1.5 h-8 bg-gradient-to-b from-orange-400/30 via-amber-400/30 to-yellow-400/30 rounded-full blur-sm"></div>
                  <div className="relative w-1.5 h-8 bg-gradient-to-b from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-md"></div>
                </>
              )}
              <div>
                <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-800'} ${isPWA ? 'drop-shadow-sm' : ''}`}>Overview</h2>
                {isPWA && (
                  <p className="text-xs text-orange-600/70 mt-0.5 font-medium">Key performance metrics</p>
                )}
              </div>
            </div>
            {statsError && (
              <button
                onClick={handleRetryStats}
                disabled={isLoadingStats}
                className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} text-amber-600 hover:text-amber-800 flex items-center gap-1.5 sm:gap-2 disabled:opacity-50`}
              >
                <FaRedo className={`${isPWA ? 'text-xs' : ''} ${isLoadingStats ? 'animate-spin' : ''}`} />
                <span className={isPWA ? 'hidden sm:inline' : ''}>Retry</span>
              </button>
            )}
          </div>

          {statsError && !isLoadingStats && (
            <div className="mx-4 mb-6 p-4 bg-orange-50 border-l-4 border-orange-400 rounded-lg">
              <div className="flex items-center">
                <FaExclamationTriangle className="text-orange-400 mr-3" />
                <div>
                  <h3 className="text-orange-800 font-semibold">Statistics Unavailable</h3>
                  <p className="text-orange-700 text-sm">{statsError.message}</p>
                </div>
              </div>
            </div>
          )}

          {isLoadingStats ? (
            <div className="text-center py-12">
              <FaSpinner className="text-4xl mx-auto text-amber-600 animate-spin mb-4" />
              <p className="text-gray-600">Loading dashboard statistics...</p>
            </div>
          ) : (
            <div className={`grid ${isPWA ? 'grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'} ${isPWA ? 'gap-3 sm:gap-4' : 'gap-4 sm:gap-6'}`}>
              {isConsultant ? (
                // Consultant stats
                <>
                  <StatCard
                    title="Pending Approvals"
                    value={stats.pendingAppointments}
                    icon={<FaHourglassHalf className="text-yellow-500" />}
                    trend={stats.pendingAppointments > 0 ? `${stats.pendingAppointments} ${stats.pendingAppointments === 1 ? 'request' : 'requests'}` : "No pending requests"}
                    color="text-yellow-600"
                    bgColor="bg-yellow-50"
                    progress={stats.pendingAppointments > 0 ? Math.min(100, (stats.pendingAppointments / 20) * 100) : 0}
                  />
                  <StatCard
                    title="Upcoming Appointments"
                    value={stats.upcomingAppointments}
                    icon={<FaCalendarAlt className="text-blue-500" />}
                    trend={stats.upcomingAppointments > 0 ? `${stats.upcomingAppointments} ${stats.upcomingAppointments === 1 ? 'appointment' : 'appointments'}` : "No upcoming appointments"}
                    color="text-blue-600"
                    bgColor="bg-blue-50"
                    progress={stats.upcomingAppointments > 0 ? Math.min(100, (stats.upcomingAppointments / 30) * 100) : 0}
                  />
                  <StatCard
                    title="Total Appointments"
                    value={stats.totalAppointments}
                    icon={<FaCheckCircle className="text-green-500" />}
                    trend={stats.totalAppointments > 0 ? `${stats.totalAppointments} total` : "No appointments yet"}
                    color="text-green-600"
                    bgColor="bg-green-50"
                    progress={stats.totalAppointments > 0 ? Math.min(100, (stats.totalAppointments / 100) * 100) : 0}
                  />
                  <StatCard
                    title="Monthly Revenue"
                    value={formatCurrency(stats.monthlyRevenue)}
                    icon={<FaMoneyBillWave className="text-purple-500" />}
                    trend={stats.monthlyRevenue > 0 ? `${formatCurrency(stats.monthlyRevenue)} this month` : "No data available"}
                    color="text-purple-600"
                    bgColor="bg-purple-50"
                    progress={stats.monthlyRevenue > 0 ? Math.min(100, (stats.monthlyRevenue / 100000) * 100) : 0}
                  />
                </>
              ) : (
                // Instructor stats
                <>
                  <StatCard
                    title="Total Students"
                    value={stats.totalStudents}
                    icon={<FaUserFriends className="text-purple-500" />}
                    trend={stats.totalStudents > 0 ? `${stats.totalStudents} ${stats.totalStudents === 1 ? 'student' : 'students'}` : "No data available"}
                    color="text-purple-600"
                    bgColor="bg-purple-50"
                    progress={stats.totalStudents > 0 ? Math.min(100, (stats.totalStudents / 50) * 100) : 0}
                  />
                  <StatCard
                    title="Active Programs"
                    value={stats.activePrograms}
                    icon={<FaClipboardList className="text-green-500" />}
                    trend={stats.activePrograms > 0 ? `${stats.activePrograms} ${stats.activePrograms === 1 ? 'program' : 'programs'}` : "No data available"}
                    color="text-green-600"
                    bgColor="bg-green-50"
                    progress={stats.activePrograms > 0 ? Math.min(100, (stats.activePrograms / 20) * 100) : 0}
                  />
                  <StatCard
                    title="Upcoming Classes"
                    value={stats.upcomingClasses}
                    icon={<FaCalendarAlt className="text-blue-500" />}
                    trend={stats.upcomingClasses > 0 ? `${stats.upcomingClasses} ${stats.upcomingClasses === 1 ? 'class' : 'classes'}` : "No upcoming classes"}
                    color="text-blue-600"
                    bgColor="bg-blue-50"
                    progress={stats.upcomingClasses > 0 ? Math.min(100, (stats.upcomingClasses / 30) * 100) : 0}
                  />
                  <StatCard
                    title="Monthly Revenue"
                    value={formatCurrency(stats.monthlyRevenue)}
                    icon={<FaMoneyBillWave className="text-yellow-500" />}
                    trend={stats.monthlyRevenue > 0 ? `${formatCurrency(stats.monthlyRevenue)} this month` : "No data available"}
                    color="text-yellow-600"
                    bgColor="bg-yellow-50"
                    progress={stats.monthlyRevenue > 0 ? Math.min(100, (stats.monthlyRevenue / 100000) * 100) : 0}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Quick Actions / Navigation Section */}
      {isPWA && (
        <section className={`py-8 sm:py-10 pt-12 sm:pt-14 bg-gradient-to-b from-white via-orange-50/30 to-white relative`}>
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-200/40 to-transparent"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`bg-gradient-to-br from-white via-orange-50/40 to-blue-50/20 rounded-2xl shadow-2xl border-2 border-orange-200/50 p-5 sm:p-6 transition-all duration-300 hover:shadow-3xl relative overflow-hidden`}>
              {/* Multiple decorative gradient overlays */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-br from-orange-200/30 to-amber-200/20 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-gradient-to-tr from-indigo-200/20 to-purple-200/10 rounded-full blur-3xl pointer-events-none"></div>

              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/50 to-transparent pointer-events-none"></div>

              <div className="flex items-center gap-3 mb-7 sm:mb-8 relative z-10">
                <div className="relative">
                  <div className="absolute left-0 w-1.5 h-8 bg-gradient-to-b from-orange-400/40 via-amber-400/40 to-yellow-400/40 rounded-full blur-sm"></div>
                  <div className="relative w-1.5 h-8 bg-gradient-to-b from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-lg"></div>
                </div>
                <div>
                  <h2 className={`text-xl sm:text-2xl font-bold text-orange-900 drop-shadow-sm`}>Quick Actions</h2>
                  <p className="text-xs text-orange-600/70 mt-0.5 font-medium">Access key features</p>
                </div>
              </div>

              <div className="flex flex-nowrap gap-3 sm:gap-4 overflow-x-auto overflow-y-visible pb-3 scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0 pt-2 relative z-10">
                {navigationItems.map((item, index) => (
                  <Link
                    key={index}
                    to={item.link}
                    className={`${item.bgColor || 'bg-white'} ${item.textColor || 'text-gray-700'} p-4 sm:p-5 rounded-2xl shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border-2 ${item.borderColor || 'border-orange-200/60'} hover:border-orange-400/70 active:scale-95 flex flex-col items-center text-center group relative overflow-visible flex-shrink-0 min-w-[110px] sm:min-w-[130px] z-10`}
                  >
                    {/* Top accent line on card */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                    {/* Decorative gradient overlay */}
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-200/30 to-amber-200/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0 pointer-events-none"></div>

                    {/* Icon container with enhanced styling */}
                    <div className={`${item.iconBg || 'bg-gray-100'} p-3 sm:p-4 rounded-xl mb-3 group-hover:scale-110 transition-transform duration-300 shadow-lg ring-2 ring-orange-100/60 group-hover:ring-orange-200/80 relative z-10`}>
                      <div className="text-2xl sm:text-3xl">
                        {item.icon}
                      </div>
                    </div>

                    {/* Title with enhanced styling */}
                    <h3 className="text-[11px] sm:text-xs font-bold relative z-10 leading-tight text-center">{item.title}</h3>

                    {/* Bottom accent on hover */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isPWA ? 'py-4' : 'py-8'} grid grid-cols-1 ${isPWA ? 'lg:grid-cols-2' : 'lg:grid-cols-3'} gap-6 ${isPWA ? 'sm:gap-4' : 'gap-8'}`}>
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Quick Actions - Non-PWA only */}
          {!isPWA && (
            <div className={`bg-white rounded-xl shadow-sm p-6 transition-all duration-300 hover:shadow-md`}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Quick Actions</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickActions.map((action, index) => (
                  <Link
                    key={index}
                    to={action.link}
                    className={`flex flex-col items-center p-4 rounded-lg transition-all duration-300 hover:shadow-md ${action.bgColor} hover:transform hover:-translate-y-1`}
                  >
                    <div className={`p-3 rounded-full ${action.bgColor} ${action.textColor} mb-3`}>
                      {action.icon}
                    </div>
                    <h3 className="font-semibold text-center text-sm leading-tight">{action.title}</h3>
                    {action.desc && (
                      <p className="text-xs text-center text-gray-500 mt-1">{action.desc}</p>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* My Clients (for consultants) / Top Students (for instructors) */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-orange-50/30 to-white' : 'bg-white'} rounded-2xl shadow-xl border-2 ${isPWA ? 'border-orange-200/50' : 'border-gray-100'} ${isPWA ? 'p-5 sm:p-6' : 'p-6'} transition-all duration-300 hover:shadow-2xl relative overflow-hidden`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-2xl"></div>
              </>
            )}
            <div className={`flex justify-between items-center ${isPWA ? 'mb-5 sm:mb-6' : 'mb-6'} relative z-10`}>
              <div className="flex items-center gap-3">
                {isPWA && (
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-orange-400/40 via-amber-400/40 to-yellow-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-7 bg-gradient-to-b from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-md"></div>
                  </div>
                )}
                <div>
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-800'} ${isPWA ? 'drop-shadow-sm' : ''}`}>{isConsultant ? (isPWA ? 'Clients' : 'My Clients') : (isPWA ? 'Students' : 'Top Students')}</h2>
                  {isPWA && (
                    <p className="text-xs text-orange-600/70 mt-0.5 font-medium">{isConsultant ? 'Your active clients' : 'Your active members'}</p>
                  )}
                </div>
              </div>
              <Link to={isConsultant ? "/trainer/appointments" : "/trainer/students"} className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 hover:text-orange-800' : 'text-amber-600 hover:underline'} font-semibold flex items-center gap-1 ${isPWA ? 'bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200/50 hover:bg-orange-100 transition-colors' : ''}`}>
                {isPWA ? 'All' : 'View All'} <FaChevronRight className={isPWA ? 'text-xs' : 'ml-1'} />
              </Link>
            </div>

            {/* Loading State */}
            {isLoadingStudents && (
              <div className="text-center py-8">
                <FaSpinner className="text-3xl mx-auto text-orange-600 animate-spin mb-2" />
                <p className="text-sm text-gray-600">{isConsultant ? 'Loading clients...' : 'Loading students...'}</p>
              </div>
            )}

            {/* Error State */}
            {studentsError && !isLoadingStudents && (
              <div className="text-center py-8">
                <FaExclamationTriangle className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{studentsError}</p>
              </div>
            )}

            {/* Empty State */}
            {!studentsError && !isLoadingStudents && featuredStudents.length === 0 && (
              <div className="text-center py-8">
                <FaUserFriends className="text-4xl text-gray-400 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">{isConsultant ? 'No Clients Yet' : 'No Students Yet'}</h3>
                <p className="text-xs text-gray-500 mb-4">{isConsultant ? 'Clients will appear here once they book appointments' : 'Start enrolling students to see them here'}</p>
                <Link
                  to={isConsultant ? "/trainer/appointments" : "/trainer/students"}
                  className="inline-block text-sm text-orange-600 hover:text-orange-800 font-medium"
                >
                  {isConsultant ? 'View Appointments →' : 'Manage Students →'}
                </Link>
              </div>
            )}

            {/* Students List */}
            {!studentsError && !isLoadingStudents && featuredStudents.length > 0 && (
              <div className={`${isPWA ? 'space-y-3 sm:space-y-4' : 'space-y-4'} relative z-10`}>
                {featuredStudents.map((student, index) => (
                  <div
                    key={index}
                    className={`${isPWA ? 'flex flex-col p-4 sm:p-5 bg-gradient-to-br from-white to-orange-50/20 shadow-lg border-2 border-orange-100/50 rounded-xl hover:shadow-xl hover:border-orange-200/70 transition-all duration-300 group relative overflow-hidden' : 'flex items-center p-4 border rounded-lg hover:bg-gray-50'} transition-colors`}
                  >
                    {isPWA && (
                      <>
                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </>
                    )}
                    {isPWA ? (
                      <>
                        <div className="flex items-center mb-3 relative z-10">
                          <div className="relative">
                            {student.image ? (
                              <img
                                src={student.image}
                                alt={student.name}
                                className="w-14 h-14 rounded-full object-cover border-[3px] border-orange-200 shadow-md flex-shrink-0 ring-2 ring-orange-100"
                                onError={(e) => {
                                  // Hide image on error and show fallback
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center border-[3px] border-orange-200 shadow-md flex-shrink-0 ring-2 ring-orange-100 ${student.image ? 'hidden' : ''}`}
                            >
                              <span className="text-white font-bold text-lg">{student.name ? student.name.charAt(0).toUpperCase() : (isConsultant ? 'C' : 'S')}</span>
                            </div>
                            {isConsultant && student.upcomingAppointments > 0 && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-blue-400 border-2 border-white rounded-full shadow-lg"></div>
                            )}
                            {!isConsultant && (
                              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-400 border-2 border-white rounded-full shadow-lg"></div>
                            )}
                          </div>
                          <div className="ml-3 flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="text-sm font-bold text-gray-900 truncate">{student.name}</h3>
                              {!isConsultant && (
                                <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 font-semibold shadow-sm ${student.level === 'Beginner' ? 'bg-gradient-to-r from-orange-100 to-orange-200 text-orange-800 border border-orange-300/50' :
                                  student.level === 'Intermediate' ? 'bg-gradient-to-r from-purple-100 to-purple-200 text-purple-800 border border-purple-300/50' :
                                    'bg-gradient-to-r from-amber-100 to-amber-200 text-amber-800 border border-amber-300/50'
                                  }`}>
                                  {student.level}
                                </span>
                              )}
                              {isConsultant && student.upcomingAppointments > 0 && (
                                <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 font-semibold shadow-sm bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300/50">
                                  {student.upcomingAppointments} upcoming
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-orange-700 font-medium truncate mt-0.5">{student.program}</p>
                          </div>
                        </div>
                        <div className="space-y-2.5 relative z-10">
                          {isConsultant ? (
                            <>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600 font-medium">Total Appointments</span>
                                  <span className="font-bold text-orange-700">{student.totalAppointments || 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-gray-600 font-medium">Total Revenue</span>
                                  <span className="font-bold text-green-700">{formatCurrency(student.totalRevenue || 0)}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <FaCalendarAlt className="text-teal-500" />
                                <span>Last appointment: <span className="font-semibold text-gray-700">{student.lastAppointment || 'No appointments'}</span></span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 font-medium">Progress ({student.sessions} sessions)</span>
                                <span className="font-bold text-orange-700">{student.progress}%</span>
                              </div>
                              <div className="w-full bg-orange-100/50 rounded-full h-2.5 shadow-inner border border-orange-200/30">
                                <div
                                  className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 h-2.5 rounded-full transition-all duration-500 shadow-md"
                                  style={{ width: `${student.progress}%` }}
                                ></div>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <FaCalendarAlt className="text-teal-500" />
                                <span>Last session: <span className="font-semibold text-gray-700">{student.lastSession}</span></span>
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        {student.image ? (
                          <img
                            src={student.image}
                            alt={student.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-yellow-200 group-hover:border-yellow-300 transition-colors flex-shrink-0 mr-4"
                            onError={(e) => {
                              // Hide image on error and show fallback
                              e.target.style.display = 'none';
                              const parent = e.target.parentElement;
                              const fallback = parent?.querySelector('.student-fallback-avatar');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div
                          className={`student-fallback-avatar w-12 h-12 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center border-2 border-yellow-200 group-hover:border-yellow-300 transition-colors flex-shrink-0 mr-4 ${student.image ? 'hidden' : ''}`}
                        >
                          <span className="text-white font-bold text-lg">{student.name ? student.name.charAt(0).toUpperCase() : (isConsultant ? 'C' : 'S')}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center">
                            <h3 className="font-semibold truncate">{student.name}</h3>
                            {!isConsultant && (
                              <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${student.level === 'Beginner' ? 'bg-amber-100 text-amber-800' :
                                student.level === 'Intermediate' ? 'bg-purple-100 text-purple-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                {student.level}
                              </span>
                            )}
                            {isConsultant && student.upcomingAppointments > 0 && (
                              <span className="text-xs px-2 py-1 rounded-full flex-shrink-0 bg-blue-100 text-blue-800">
                                {student.upcomingAppointments} upcoming
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 truncate">{student.program}</p>
                          {isConsultant ? (
                            <div className="mt-2 space-y-1">
                              <div className="flex justify-between text-xs">
                                <span className="truncate mr-2">Total Appointments</span>
                                <span className="flex-shrink-0 font-semibold">{student.totalAppointments || 0}</span>
                              </div>
                              <div className="flex justify-between text-xs">
                                <span className="truncate mr-2">Total Revenue</span>
                                <span className="flex-shrink-0 font-semibold text-green-600">{formatCurrency(student.totalRevenue || 0)}</span>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2">
                              <div className="flex justify-between text-xs mb-1">
                                <span className="truncate mr-2">Progress ({student.sessions})</span>
                                <span className="flex-shrink-0">{student.progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-gradient-to-r from-yellow-400 to-yellow-600 h-2 rounded-full transition-all duration-500"
                                  style={{ width: `${student.progress}%` }}
                                ></div>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                          {isConsultant ? (
                            <>Last appointment: {student.lastAppointment || 'No appointments'}</>
                          ) : (
                            <>Last session: {student.lastSession}</>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Recent Activity */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-orange-50/30 to-white' : 'bg-white'} rounded-2xl shadow-xl border-2 ${isPWA ? 'border-orange-200/50' : 'border-gray-100'} ${isPWA ? 'p-5 sm:p-6' : 'p-6'} transition-all duration-300 hover:shadow-2xl relative overflow-hidden`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-400/60 to-transparent"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-2xl"></div>
              </>
            )}
            <div className={`flex justify-between items-center ${isPWA ? 'mb-5 sm:mb-6' : 'mb-6'} relative z-10`}>
              <div className="flex items-center gap-3">
                {isPWA && (
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-orange-400/40 via-amber-400/40 to-yellow-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-7 bg-gradient-to-b from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-md"></div>
                  </div>
                )}
                <div>
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-orange-900' : 'text-gray-800'} ${isPWA ? 'drop-shadow-sm' : ''}`}>{isPWA ? 'Activity' : 'Recent Activity'}</h2>
                  {isPWA && (
                    <p className="text-xs text-orange-600/70 mt-0.5 font-medium">Recent transactions</p>
                  )}
                </div>
              </div>
              <Link to="/trainer/fees" className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-orange-700 hover:text-orange-800' : 'text-amber-600 hover:underline'} font-semibold flex items-center gap-1 ${isPWA ? 'bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200/50 hover:bg-orange-100 transition-colors' : ''}`}>
                {isPWA ? 'All' : 'View All'} <FaChevronRight className={isPWA ? 'text-xs' : 'ml-1'} />
              </Link>
            </div>

            {/* Loading State */}
            {isLoadingActivities && (
              <div className="text-center py-8">
                <FaSpinner className="text-3xl mx-auto text-orange-600 animate-spin mb-2" />
                <p className="text-sm text-gray-600">Loading activities...</p>
              </div>
            )}

            {/* Error State */}
            {activitiesError && !isLoadingActivities && (
              <div className="text-center py-8">
                <FaExclamationTriangle className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{activitiesError}</p>
              </div>
            )}

            {/* Empty State */}
            {!activitiesError && !isLoadingActivities && activities.length === 0 && (
              <div className="text-center py-8">
                <FaInbox className="text-4xl text-gray-400 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">No Recent Activity</h3>
                <p className="text-xs text-gray-500 mb-4">Your recent appointments and payments will appear here</p>
                <Link
                  to="/trainer/fees"
                  className="inline-block text-sm text-orange-600 hover:text-orange-800 font-medium"
                >
                  View Payments →
                </Link>
              </div>
            )}

            {/* Activities List */}
            {!activitiesError && !isLoadingActivities && activities.length > 0 && (
              <div className={`${isPWA ? 'space-y-3 sm:space-y-4' : 'space-y-4'} relative z-10`}>
                {activities.map((activity, index) => (
                  <div
                    key={index}
                    className={isPWA ? `p-4 bg-gradient-to-br from-white to-orange-50/20 shadow-lg border-2 border-orange-100/50 rounded-xl hover:shadow-xl hover:border-orange-200/70 transition-all duration-300 group relative overflow-hidden` : `flex items-start border-l-2 border-blue-200 pl-4 py-2 hover:bg-gray-50 rounded transition-colors`}
                  >
                    {isPWA && (
                      <>
                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-orange-100/20 to-amber-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </>
                    )}
                    {isPWA ? (
                      <div className="flex items-start gap-3 relative z-10">
                        <div className={`rounded-xl p-2.5 sm:p-3 flex-shrink-0 shadow-md ring-2 ring-opacity-50 ${activity.type === 'payment' ? 'bg-gradient-to-br from-green-100 to-emerald-100 text-green-700 ring-green-200' :
                          activity.type === 'class' ? 'bg-gradient-to-br from-orange-100 to-amber-100 text-orange-700 ring-orange-200' :
                            'bg-gradient-to-br from-yellow-100 to-orange-100 text-yellow-700 ring-yellow-200'
                          }`}>
                          {activity.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 mb-1.5">
                            {activity.type === 'payment' ? `${activity.student} paid ₹${activity.amount}` :
                              activity.type === 'class' ? `Class with ${activity.student}${activity.program ? ` (${activity.program})` : ''}` :
                                `${activity.student} ${activity.milestone || ''}`}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <FaCalendarAlt className="text-orange-500 text-xs" />
                              <p className="text-xs text-gray-600 font-medium">{activity.time}</p>
                            </div>
                            <span className={`text-[10px] px-2.5 py-1 rounded-full flex-shrink-0 font-semibold shadow-sm ${activity.status === 'completed' ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-300/50' :
                              activity.status === 'pending' ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-300/50' :
                                'bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border border-purple-300/50'
                              }`}>
                              {activity.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={`rounded-full p-2 mr-3 flex-shrink-0 ${activity.type === 'payment' ? 'bg-green-100 text-green-600' :
                          activity.type === 'class' ? 'bg-amber-100 text-amber-600' :
                            'bg-purple-100 text-purple-600'
                          }`}>
                          {activity.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {activity.type === 'payment' ? `${activity.student} paid ₹${activity.amount}` :
                              activity.type === 'class' ? `Class with ${activity.student}${activity.program ? ` (${activity.program})` : ''}` :
                                `${activity.student} ${activity.milestone || ''}`}
                          </p>
                          <div className="flex justify-between items-center mt-1">
                            <p className="text-xs text-gray-500 truncate mr-2">{activity.time}</p>
                            <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${activity.status === 'completed' ? 'bg-green-100 text-green-800' :
                              activity.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                              {activity.status}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Schedule */}
          <div className={`${isPWA ? 'bg-gradient-to-br from-white via-yellow-50/30 to-white' : 'bg-white'} rounded-2xl shadow-xl border-2 ${isPWA ? 'border-yellow-200/50' : 'border-gray-100'} ${isPWA ? 'p-5 sm:p-6' : 'p-6'} transition-all duration-300 hover:shadow-2xl relative overflow-hidden`}>
            {isPWA && (
              <>
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-yellow-400/60 to-transparent"></div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-100/20 to-orange-100/10 rounded-full blur-2xl"></div>
              </>
            )}
            <div className={`flex justify-between items-center ${isPWA ? 'mb-5 sm:mb-6' : 'mb-6'} relative z-10`}>
              <div className="flex items-center gap-3">
                {isPWA && (
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-7 bg-gradient-to-b from-yellow-400/40 via-orange-400/40 to-amber-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-7 bg-gradient-to-b from-yellow-500 via-orange-500 to-amber-500 rounded-full shadow-md"></div>
                  </div>
                )}
                <div>
                  <h2 className={`${isPWA ? 'text-xl sm:text-2xl' : 'text-2xl'} font-bold ${isPWA ? 'text-yellow-900' : 'text-gray-800'} ${isPWA ? 'drop-shadow-sm' : ''}`}>{isPWA ? 'Upcoming' : (isConsultant ? 'Upcoming Appointments' : 'Upcoming Classes')}</h2>
                  {isPWA && (
                    <p className="text-xs text-yellow-600/70 mt-0.5 font-medium">{isConsultant ? 'Your appointments' : 'Your schedule'}</p>
                  )}
                </div>
              </div>
              <Link to={isConsultant ? "/trainer/appointments" : "/trainer/classes"} className={`${isPWA ? 'text-xs sm:text-sm' : 'text-sm'} ${isPWA ? 'text-yellow-700 hover:text-yellow-800' : 'text-amber-600 hover:underline'} font-semibold flex items-center gap-1 ${isPWA ? 'bg-yellow-50 px-3 py-1.5 rounded-lg border border-yellow-200/50 hover:bg-yellow-100 transition-colors' : ''}`}>
                {isPWA ? 'All' : (isConsultant ? 'View All Appointments' : 'View All Classes')} <FaChevronRight className={isPWA ? 'text-xs' : 'ml-1'} />
              </Link>
            </div>

            {/* Loading State */}
            {isLoadingUpcomingClasses && (
              <div className="text-center py-8">
                <FaSpinner className="text-3xl mx-auto text-orange-600 animate-spin mb-2" />
                <p className="text-sm text-gray-600">{isConsultant ? 'Loading upcoming appointments...' : 'Loading upcoming classes...'}</p>
              </div>
            )}

            {/* Error State */}
            {upcomingClassesError && !isLoadingUpcomingClasses && (
              <div className="text-center py-8">
                <FaExclamationTriangle className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">{upcomingClassesError}</p>
              </div>
            )}

            {/* Empty State */}
            {!upcomingClassesError && !isLoadingUpcomingClasses && upcomingClasses.length === 0 && (
              <div className="text-center py-8">
                <FaCalendarAlt className="text-4xl text-gray-400 mx-auto mb-3" />
                <h3 className="text-sm font-medium text-gray-900 mb-1">{isConsultant ? 'No Upcoming Appointments' : 'No Upcoming Classes'}</h3>
                <p className="text-xs text-gray-500 mb-4">{isConsultant ? 'Book appointments to see them here' : 'Schedule your classes to see them here'}</p>
                <Link
                  to={isConsultant ? "/trainer/appointments" : "/trainer/classes"}
                  className="inline-block text-sm text-orange-600 hover:text-orange-800 font-medium"
                >
                  {isConsultant ? 'View Appointments →' : 'Create Classes →'}
                </Link>
              </div>
            )}

            {/* Upcoming Classes/Appointments List */}
            {!upcomingClassesError && !isLoadingUpcomingClasses && upcomingClasses.length > 0 && (
              <div className={`${isPWA ? 'space-y-3 sm:space-y-4' : 'space-y-4'} relative z-10`}>
                {upcomingClasses.map((classItem, index) => {
                  const colors = [
                    { bg: 'bg-gradient-to-br from-orange-50 to-amber-50', border: 'border-orange-300/60', text: 'text-orange-700', cardBg: 'bg-gradient-to-br from-white to-orange-50/30', iconBg: 'bg-gradient-to-br from-orange-100 to-amber-100' },
                    { bg: 'bg-gradient-to-br from-amber-50 to-yellow-50', border: 'border-amber-300/60', text: 'text-amber-700', cardBg: 'bg-gradient-to-br from-white to-amber-50/30', iconBg: 'bg-gradient-to-br from-amber-100 to-yellow-100' },
                    { bg: 'bg-gradient-to-br from-yellow-50 to-orange-50', border: 'border-yellow-300/60', text: 'text-yellow-700', cardBg: 'bg-gradient-to-br from-white to-yellow-50/30', iconBg: 'bg-gradient-to-br from-yellow-100 to-orange-100' }
                  ];
                  const color = colors[index % colors.length];
                  const formattedDate = formatUpcomingDate(classItem.nextOccurrence || classItem.appointmentDate);
                  // Extract time from nextOccurrence/appointmentDate and format using user's time format
                  const itemDate = new Date(classItem.nextOccurrence || classItem.appointmentDate);
                  const timeString = `${itemDate.getHours().toString().padStart(2, '0')}:${itemDate.getMinutes().toString().padStart(2, '0')}`;
                  const formattedTime = formatTimeUtil(timeString, dateTimeSettings.timeFormat);

                  // For appointments: use clientName and slotDuration; for classes: use classType and classDuration
                  const displayName = isConsultant ? (classItem.clientName || 'Client') : (classItem.classType || 'Class');
                  const displayDuration = isConsultant ? (classItem.slotDuration || '1 hour') : (classItem.classDuration || '');

                  return (
                    <div key={classItem._id || index} className={isPWA ? `p-4 sm:p-5 ${color.cardBg} shadow-lg border-2 ${color.border} rounded-xl hover:shadow-xl hover:border-opacity-80 transition-all duration-300 group relative overflow-hidden` : `${isPWA ? 'p-3' : 'p-4'} ${color.bg} rounded-lg border-l-4 ${color.border} transition-all hover:shadow-sm`}>
                      {isPWA && (
                        <>
                          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-yellow-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-yellow-100/20 to-orange-100/10 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </>
                      )}
                      {isPWA ? (
                        <div className="space-y-3 relative z-10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`${color.iconBg} p-2 rounded-lg shadow-md ring-2 ring-opacity-50 ${color.border.replace('/60', '/30')}`}>
                                <FaCalendarAlt className={`${color.text} text-sm`} />
                              </div>
                              <span className="text-sm font-bold text-gray-900">{displayName}</span>
                            </div>
                            {displayDuration && (
                              <span className="text-[10px] px-2.5 py-1 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-full font-semibold shadow-sm border border-gray-300/50">
                                {displayDuration}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-orange-700 font-medium ml-11">{classItem.programName || 'Consultation'}</p>
                          <div className={`flex items-center gap-2.5 pt-3 border-t-2 ${color.border.replace('/60', '/30')} ml-11`}>
                            <div className="flex items-center gap-1.5">
                              <FaCalendarAlt className={`${color.text} text-xs`} />
                              <span className={`text-xs ${color.text} font-bold`}>
                                {formattedDate}
                              </span>
                            </div>
                            <span className="text-gray-300">•</span>
                            <div className="flex items-center gap-1.5">
                              <span className={`text-xs ${color.text} font-bold`}>
                                {formattedTime}
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex justify-between items-start">
                            <span className="font-medium truncate">{displayName}</span>
                            <span className={`text-sm ${color.text} whitespace-nowrap ml-2`}>
                              {formattedDate}, {formattedTime}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1 truncate">{classItem.programName || 'Consultation'}</p>
                          {displayDuration && (
                            <div className="flex gap-2 mt-2">
                              <span className="text-xs px-2 py-1 bg-white rounded-full">{displayDuration}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Teaching Goals */}
          <div className={`bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-700 rounded-2xl shadow-2xl ${isPWA ? 'p-4 sm:p-5' : 'p-6'} text-white relative overflow-hidden border-2 border-orange-400/30`}>
            {/* Decorative gradient overlays */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-orange-400/30 to-amber-400/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-yellow-400/20 to-orange-400/10 rounded-full blur-2xl"></div>

            {/* Top accent line */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-orange-300/60 to-transparent"></div>

            <div className="relative z-10">
              {/* Header */}
              <div className={`flex items-center gap-2 ${isPWA ? 'mb-3 sm:mb-4' : 'mb-4'}`}>
                {isPWA && (
                  <div className="relative">
                    <div className="absolute left-0 w-1.5 h-6 bg-gradient-to-b from-orange-400/40 via-amber-400/40 to-yellow-400/40 rounded-full blur-sm"></div>
                    <div className="relative w-1.5 h-6 bg-gradient-to-b from-orange-500 via-amber-500 to-yellow-500 rounded-full shadow-md"></div>
                  </div>
                )}
                <div className={`${isPWA ? 'bg-gradient-to-br from-orange-100 to-amber-100 p-2 rounded-lg shadow-md ring-1 ring-orange-100/50' : 'bg-white/20 backdrop-blur-sm p-2 rounded-lg shadow-lg ring-2 ring-white/20'}`}>
                  <FaMedal className={`${isPWA ? 'text-orange-600 text-base' : 'text-yellow-300 text-xl sm:text-2xl'}`} />
                </div>
                <div>
                  <h2 className={`${isPWA ? 'text-lg sm:text-xl' : 'text-xl'} font-bold ${isPWA ? 'text-orange-900 drop-shadow-sm' : 'drop-shadow-md'}`}>{isConsultant ? 'Monthly Appointments Goal' : 'Monthly Teaching Goal'}</h2>
                </div>
              </div>

              {(() => {
                const currentCount = isConsultant ? (stats.totalAppointments || 0) : (stats.sessionsTaught || 0);
                const remainingCount = Math.max(0, MONTHLY_GOAL - currentCount);
                const completionPercentage = Math.min(100, Math.round((currentCount / MONTHLY_GOAL) * 100));
                const isGoalAchieved = completionPercentage >= 100;
                const unitLabel = isConsultant ? 'appointment' : 'session';
                const unitLabelPlural = isConsultant ? 'appointments' : 'sessions';

                return (
                  <>
                    {/* Compact Progress Display */}
                    <div className={`${isPWA ? 'bg-gradient-to-br from-white to-orange-50/20 rounded-lg p-3 sm:p-4 mb-3 border-2 border-orange-100/50 shadow-md' : 'bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4 border border-white/20'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-baseline gap-2">
                          <span className={`${isPWA ? 'text-2xl sm:text-3xl text-orange-800' : 'text-3xl sm:text-4xl text-white'} font-bold`}>{completionPercentage}%</span>
                          <span className={`${isPWA ? 'text-xs text-orange-600/70' : 'text-sm text-orange-100/80'}`}>{currentCount}/{MONTHLY_GOAL}</span>
                        </div>
                        {!isGoalAchieved && (
                          <div className={`${isPWA ? 'text-xs text-orange-700' : 'text-sm text-white'} font-medium`}>
                            {remainingCount} left
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      <div className="relative">
                        <div className={`w-full ${isPWA ? 'bg-orange-100/50' : 'bg-white/20 backdrop-blur-sm'} rounded-full ${isPWA ? 'h-3' : 'h-4'} shadow-inner overflow-hidden border ${isPWA ? 'border-orange-200/30' : 'border-white/10'}`}>
                          <div
                            className={`${isPWA ? 'bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500' : 'bg-gradient-to-r from-yellow-300 via-amber-300 to-orange-300'} rounded-full transition-all duration-1000 shadow-lg ${isPWA ? 'h-3' : 'h-4'} relative overflow-hidden`}
                            style={{ width: `${completionPercentage}%` }}
                          >
                            {completionPercentage > 0 && (
                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-pulse"></div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Compact Status */}
                    {!isGoalAchieved && (
                      <div className={`${isPWA ? 'bg-gradient-to-br from-orange-50 to-amber-50/50 rounded-lg p-2 sm:p-3 border-2 border-orange-200/50' : 'bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20'}`}>
                        <div className="flex items-center gap-2">
                          <FaFire className={`${isPWA ? 'text-orange-500 text-sm' : 'text-amber-300'} ${isPWA ? '' : 'text-base'}`} />
                          <p className={`${isPWA ? 'text-xs text-orange-700' : 'text-sm text-white'} font-medium`}>
                            <span className="font-bold">{remainingCount}</span> more {remainingCount === 1 ? unitLabel : unitLabelPlural} needed
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Switch Confirmation Modal */}
      {showSwitchConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white p-6 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <FaBriefcase className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Switch Dashboard View</h2>
                  <p className="text-white/90 text-sm">Confirm your dashboard switch</p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="text-orange-500 text-xl mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-700 font-medium mb-2">Switch to Member Dashboard?</p>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    You're about to switch from your <strong>Professional Dashboard</strong> to your <strong>Member Dashboard</strong>.
                    This will take you to your personal view where you can manage your classes, track your progress, and access member features.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowSwitchConfirm(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSwitchConfirm(false);
                  navigate('/student/home');
                }}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
              >
                Switch to Member View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Enhanced StatCard component with animation
function StatCard({ title, value, icon, trend, color, bgColor, progress }) {
  const [animatedValue, setAnimatedValue] = useState(0);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    if (typeof value === 'number') {
      const duration = 1000; // Animation duration in ms
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const currentValue = Math.floor(progress * value);

        setAnimatedValue(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [value]);

  if (isPWA) {
    // Professional app-like design for PWA mode
    const getGradientClass = (bgColor) => {
      if (bgColor.includes('blue')) return 'from-teal-100/50 via-blue-100/40 to-indigo-100/30';
      if (bgColor.includes('green')) return 'from-teal-100/50 via-emerald-100/40 to-teal-100/30';
      if (bgColor.includes('purple')) return 'from-indigo-100/50 via-purple-100/40 to-indigo-100/30';
      if (bgColor.includes('yellow')) return 'from-amber-100/50 via-yellow-100/40 to-orange-100/30';
      return 'from-teal-100/40 via-blue-100/30 to-indigo-100/20';
    };

    const getProgressGradient = (color) => {
      if (color.includes('blue')) return 'bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500';
      if (color.includes('green')) return 'bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600';
      if (color.includes('purple')) return 'bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600';
      if (color.includes('yellow')) return 'bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500';
      return 'bg-gradient-to-r from-teal-500 to-indigo-500';
    };

    const getCardGradient = (bgColor) => {
      if (bgColor.includes('blue')) return 'from-blue-50/80 via-teal-50/60 to-white';
      if (bgColor.includes('green')) return 'from-emerald-50/80 via-teal-50/60 to-white';
      if (bgColor.includes('purple')) return 'from-indigo-50/80 via-purple-50/60 to-white';
      if (bgColor.includes('yellow')) return 'from-amber-50/80 via-yellow-50/60 to-white';
      return 'from-teal-50/80 via-blue-50/60 to-white';
    };

    const getBorderColor = (bgColor) => {
      if (bgColor.includes('blue')) return 'border-orange-200/60';
      if (bgColor.includes('green')) return 'border-orange-200/60';
      if (bgColor.includes('purple')) return 'border-yellow-200/60';
      if (bgColor.includes('yellow')) return 'border-amber-200/60';
      return 'border-orange-200/60';
    };

    const getIconBg = (bgColor) => {
      if (bgColor.includes('blue')) return 'bg-gradient-to-br from-orange-100 to-amber-100';
      if (bgColor.includes('green')) return 'bg-gradient-to-br from-orange-100 to-amber-100';
      if (bgColor.includes('purple')) return 'bg-gradient-to-br from-indigo-100 to-purple-100';
      if (bgColor.includes('yellow')) return 'bg-gradient-to-br from-amber-100 to-yellow-100';
      return 'bg-gradient-to-br from-orange-100 to-amber-100';
    };

    return (
      <div className={`bg-gradient-to-br ${getCardGradient(bgColor)} p-4 rounded-2xl shadow-xl border-2 ${getBorderColor(bgColor)} relative overflow-hidden group hover:shadow-2xl transition-all duration-300`}>
        {/* Decorative gradient overlay */}
        <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${getGradientClass(bgColor)} rounded-full blur-2xl opacity-70 group-hover:opacity-100 transition-opacity duration-300`}></div>

        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-300/50 to-transparent"></div>

        <div className="relative z-10">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] sm:text-xs font-semibold text-orange-700/80 mb-1.5 uppercase tracking-wider">{title}</p>
              <p className={`text-2xl sm:text-3xl font-bold ${color} leading-tight drop-shadow-sm`}>
                {typeof value === 'number' ? animatedValue : value}
              </p>
            </div>
            <div className={`${getIconBg(bgColor)} ${color} p-2.5 sm:p-3 rounded-xl shadow-lg flex-shrink-0 ml-2 ring-2 ring-orange-100/50 group-hover:ring-orange-200/70 transition-all`}>
              <div className="text-lg sm:text-xl">
                {icon}
              </div>
            </div>
          </div>
          {progress !== undefined && progress !== null && (
            <div className="mt-3">
              <div className="w-full bg-orange-100/50 rounded-full h-2 overflow-hidden shadow-inner">
                <div
                  className={`${getProgressGradient(color)} h-2 rounded-full transition-all duration-1000 shadow-md`}
                  style={{ width: `${Math.min(progress, 100)}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Original design for non-PWA mode
  return (
    <div className={`p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-md ${bgColor}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs sm:text-sm text-gray-500 truncate">{title}</p>
          <p className={`text-lg sm:text-xl md:text-2xl font-bold mt-0.5 sm:mt-1 ${color} truncate`}>
            {typeof value === 'number' ? animatedValue : value}
          </p>
          {trend && (
            <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1 truncate">{trend}</p>
          )}
        </div>
        <div className={`text-xl sm:text-2xl md:text-3xl ${color} flex-shrink-0 ml-2`}>
          {icon}
        </div>
      </div>
      {trend && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-1 sm:h-1.5">
            <div
              className={`h-1 sm:h-1.5 rounded-full ${color.replace('text', 'bg')}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1 truncate">{trend}</p>
        </div>
      )}
    </div>
  );
}

export default HomeTrainer;